import React, { useState, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Breadcrumbs,
  Link,
  useMediaQuery,
  Collapse,
  Container,
} from "@mui/material";
import {
  IoMenuOutline,
  IoHomeOutline,
  IoPersonOutline,
  IoStorefrontOutline,
  IoSettingsOutline,
  IoLogOutOutline,
  IoChevronForward,
  IoChevronDownOutline,
  IoChevronForwardOutline,
} from "react-icons/io5";

import { ThemeContext } from "../../contexts/ThemeContext";
import AuthContext from "../../contexts/AuthProvider";
import useIdleTimer from "../../helpers/hooks/useIdleTimer";
// import { getRole } from "../../utils/auth";

// Sidebar width
const DRAWER_WIDTH = 280;

const MenuSection = ({
  title,
  items,
  location,
  navigate,
  isMobile,
  setMobileOpen,
  isMenuActive,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <Box
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          py: 1,
          pl: 2,
          "&:hover": {
            bgcolor: "action.hover",
            borderRadius: 1,
          },
        }}
      >
        {isOpen ? (
          <IoChevronDownOutline size={16} style={{ marginRight: "8px" }} />
        ) : (
          <IoChevronForwardOutline size={16} style={{ marginRight: "8px" }} />
        )}
        <Typography
          variant="subtitle2"
          color="textSecondary"
          sx={{
            flex: 1,
            fontWeight: isOpen ? 600 : 400,
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Collapsible Menu Items */}
      <Collapse in={isOpen} timeout="auto">
        <List>
          {items.map((item) => (
            <ListItem key={item.title} disablePadding>
              <ListItemButton
                selected={isMenuActive(item.path)}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "primary.lighter",
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isMenuActive(item.path) ? "primary.main" : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
};

const MainLayout = ({ navigationConfig }) => {
  useIdleTimer(1800000);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { authData } = useContext(AuthContext);
  const userRole = authData.role;
  const { themeMode, setThemeMode } = useContext(ThemeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Use navigationConfig instead of hardcoded arrays
  const {
    mainNavItems,
    reportNavItems,
    inventoryNavItems,
    managementNavItems,
  } = navigationConfig;

  // States
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Function to check if a menu is active
  const isMenuActive = (menuPath) => {
    // If path is exactly the same
    if (location.pathname === menuPath) return true;

    // If this is a parent path and we are on a subpath
    if (menuPath !== "/" && location.pathname.startsWith(menuPath + "/")) {
      return true;
    }

    return false;
  };

  const getMenuTitle = (path) => {
    const allNavItems = [
      ...navigationConfig.mainNavItems,
      ...navigationConfig.reportNavItems,
      ...navigationConfig.inventoryNavItems,
      ...navigationConfig.managementNavItems,
    ];

    const menuItem = allNavItems.find((item) => item.path === `/${path}`);
    return menuItem
      ? menuItem.title
      : path.charAt(0).toUpperCase() + path.slice(1);
  };

  // Handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // setAuthData(null);
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("role");
    navigate("/login");
  };

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split("/").filter((x) => x);

    // Helper untuk menentukan apakah segment path adalah ID
    const isIdSegment = (segment) => {
      // Cek apakah segment berisi hanya angka atau UUID
      return (
        /^\d+$/.test(segment) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment
        )
      );
    };

    // Helper untuk mendapatkan judul halaman yang benar
    const getPathTitle = (path, isId) => {
      if (isId) {
        // Jika segment adalah ID, gunakan judul yang lebih deskriptif
        const prevSegment = pathnames[pathnames.indexOf(path) - 1];
        if (prevSegment === "edit") return "";
        if (prevSegment === "detail") return "";
        return "";
      }
      return getMenuTitle(path);
    };

    return (
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Page Title - Jangan tampilkan ID pada judul */}
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 2, md: 0 },
              }}
            >
              {pathnames.length > 0
                ? isIdSegment(pathnames[pathnames.length - 1])
                  ? `${getMenuTitle(
                      pathnames[pathnames.length - 2]
                    )} ${getPathTitle(pathnames[pathnames.length - 1], true)}`
                  : getMenuTitle(pathnames[pathnames.length - 1])
                : "Dashboard"}
            </Typography>

            {/* Breadcrumbs */}
            <Breadcrumbs
              separator={<IoChevronForward fontSize="small" />}
              aria-label="breadcrumb"
              sx={{
                "& .MuiBreadcrumbs-separator": {
                  mx: 0.5,
                },
              }}
            >
              <Link
                color="inherit"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                }}
                sx={{
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Home
              </Link>
              {pathnames.map((path, index) => {
                const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
                const isLast = index === pathnames.length - 1;
                const isId = isIdSegment(path);
                const title = getPathTitle(path, isId);

                // Skip rendering if title is empty (ID segments)
                if (!title) return null;

                return isLast ? (
                  <Typography key={path} color="text.primary">
                    {title}
                  </Typography>
                ) : (
                  <Link
                    key={path}
                    color="inherit"
                    href={routeTo}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routeTo);
                    }}
                    sx={{
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {title}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>
        </Box>
      </Box>
    );
  };

  // Drawer content
  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Logo */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <IoStorefrontOutline size={30} color={theme.palette.primary.main} />
        <Typography variant="h6" fontWeight="bold" color="primary">
          BOTTLE GARDEN
        </Typography>
      </Box>

      <Divider />

      {/* Navigation Lists */}
      <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
        <List>
          {mainNavItems.map((item) => (
            <ListItem key={item.title} disablePadding>
              <ListItemButton
                selected={isMenuActive(item.path)}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "primary.lighter",
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isMenuActive(item.path) ? "primary.main" : "inherit",
                  }}
                >
                  {item.icon || <IoHomeOutline size={22} />}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Report Sales Section */}
        <MenuSection
          title="Report Sales"
          items={reportNavItems}
          location={location}
          navigate={navigate}
          isMobile={isMobile}
          setMobileOpen={setMobileOpen}
          isMenuActive={isMenuActive}
        />

        {/* Inventory products Section */}
        <MenuSection
          title="Inventory Products"
          items={inventoryNavItems}
          location={location}
          navigate={navigate}
          isMobile={isMobile}
          setMobileOpen={setMobileOpen}
          isMenuActive={isMenuActive}
        />

        {/* Inventory Management Section */}
        {userRole === "admin" || userRole === "owner" ? (
          <MenuSection
            title="Management"
            items={managementNavItems}
            location={location}
            navigate={navigate}
            isMobile={isMobile}
            setMobileOpen={setMobileOpen}
            isMenuActive={isMenuActive}
          />
        ) : null}
      </Box>

      {/* User Profile Section */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <ListItemButton
          onClick={handleProfileMenuOpen}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
              }}
            >
              A
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary="Admin User"
            secondary="admin@example.com"
            primaryTypographyProps={{ variant: "subtitle2" }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          boxShadow: "none",
        }}
      >
        <Toolbar>
          <IconButton
            color={theme.palette.primary.main}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <IoMenuOutline />
          </IconButton>

          {/* Header Actions */}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={authData?.name || "Admin User"}>
              <IconButton
                onClick={handleProfileMenuOpen}
                size="large"
                color={theme.palette.primary.main}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "primary.main",
                  }}
                >
                  {authData?.name?.charAt(0) || "A"}
                </Avatar>
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Settings">
              <IconButton size="large" color={theme.palette.primary.main}>
                <IoSettingsOutline />
              </IconButton>
            </Tooltip> */}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar / Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              bgcolor: "background.paper",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              bgcolor: "background.paper",
              borderRight: 1,
              borderColor: "divider",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          height: "100vh",
          bgcolor: "background.default",
          overflowY: "auto",
        }}
      >
        <Toolbar />
        <Box sx={{ height: "calc(100vh - 64px)", overflowY: "auto" }}>
          {getBreadcrumbs()}
          <Box sx={{ p: 3 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => navigate("/profile")}>
          <ListItemIcon>
            <IoPersonOutline size={20} />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => navigate("/settings")}>
          <ListItemIcon>
            <IoSettingsOutline size={20} />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <IoLogOutOutline size={20} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MainLayout;
